import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import SEOHead from '../components/SEOHead';
import { BlogZoomableImage } from '../components/BlogZoomableImage';
import CallbackForm from '../components/CallbackForm';
import { allPosts, blogPosts } from '../data/blogPosts';
import { newsAPI } from '../lib/api';
import { mergeBlogPostWithPublication } from '../lib/blogPostMerge';
import { NEWS_HUB_PATH } from '../lib/newsPublications';

const formatContent = (content) => {
  if (!content) return null;
  return content.split('\n\n').map((paragraph, idx) => (
    <p key={idx} className="mb-4 text-lg text-muted-foreground leading-relaxed">
      {paragraph.trim()}
    </p>
  ));
};

function ChecklistCtaBlock({ path = '/ready-for-ai', compact = false }) {
  return (
    <div
      className={`${compact ? 'my-6 p-6' : 'mb-10 p-8'} bg-gradient-to-r from-accent/10 to-accent/5 rounded-2xl border border-accent/20`}
    >
      <p className={`${compact ? 'text-base' : 'text-lg'} text-muted-foreground mb-6`}>
        Пройдите бесплатный чек-лист готовности к AI-внедрению: данные, владелец процесса,
        интеграции.
      </p>
      <Link
        to={path}
        className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-accent-foreground font-medium rounded-lg hover:bg-accent/90 transition-colors"
      >
        Пройти чек-лист готовности к AI-внедрению
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </Link>
    </div>
  );
}

function ConsultationCtaBlock({ compact = false, onOpen }) {
  return (
    <div
      className={`${compact ? 'my-6 p-6' : 'mb-10 p-8'} bg-gradient-to-r from-accent/10 to-accent/5 rounded-2xl border border-accent/20`}
    >
      <p className={`${compact ? 'text-base' : 'text-lg'} text-muted-foreground mb-6`}>
        Начать с бесплатной консультации с экспертом
      </p>
      <button
        type="button"
        onClick={onOpen}
        className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-accent-foreground font-medium rounded-lg hover:bg-accent/90 transition-colors"
      >
        Начать с бесплатной консультации
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
}

function DiscussAiCtaBlock({ href, compact = false }) {
  return (
    <div
      className={`${compact ? 'my-6 p-6' : 'mb-10 p-8'} bg-gradient-to-r from-accent/10 to-accent/5 rounded-2xl border border-accent/20`}
    >
      <p className={`${compact ? 'text-base' : 'text-lg'} text-muted-foreground mb-6`}>
        Обсудим архитектуру harness и AI-агентов под ваш контур: ИБ, бюджет, on-prem или гибрид.
      </p>
      <Link
        to={href}
        className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-accent-foreground font-medium rounded-lg hover:bg-accent/90 transition-colors"
      >
        Обсудить внедрение
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </Link>
    </div>
  );
}

function renderDataTable(dataTable) {
  if (!dataTable?.headers?.length || !dataTable?.rows?.length) return null;
  return (
    <div className="overflow-x-auto my-6">
      <table className="w-full border-collapse text-base">
        <thead>
          <tr className="border-b border-border">
            {dataTable.headers.map((header, i) => (
              <th key={i} className="text-left py-3 px-4 font-semibold">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataTable.rows.map((row, rowIndex) => (
            <tr key={rowIndex} className="border-b border-border/50">
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="py-3 px-4 text-muted-foreground align-top">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const BlogPostPage = () => {
  const { slug } = useParams();
  const staticPost = blogPosts[slug];
  const [publication, setPublication] = useState(null);
  const [publicationLoading, setPublicationLoading] = useState(true);
  const [consultationOpen, setConsultationOpen] = useState(false);

  useEffect(() => {
    if (!slug) return undefined;
    let active = true;
    setPublicationLoading(true);
    newsAPI.getPublication(slug).then((data) => {
      if (active) {
        setPublication(data);
        setPublicationLoading(false);
      }
    }).catch(() => {
      if (active) {
        setPublication(null);
        setPublicationLoading(false);
      }
    });
    return () => { active = false; };
  }, [slug]);

  const siteBase = (process.env.REACT_APP_BASE_URL || 'https://antologia.tech').replace(/\/$/, '');
  const post = useMemo(
    () => mergeBlogPostWithPublication(staticPost, publication, siteBase, {
      publicationResolved: !publicationLoading,
    }),
    [staticPost, publication, publicationLoading, siteBase],
  );
  
  const currentIndex = allPosts.findIndex(p => p.slug === slug);
  const nextPost = currentIndex >= 0 && currentIndex < allPosts.length - 1 ? allPosts[currentIndex + 1] : null;
  const checklistPath = post?.checklistPath || '/ready-for-ai';
  const useConsultationCta =
    slug === 'klassifikaciya-ii-reshenij-chat-rag-agent-harness';
  const useDiscussAiCta = Boolean(post?.useDiscussAiCta);
  const discussAiHref =
    post?.conclusionCta?.href || '/discuss-ai?utm_source=blog&utm_medium=organic';
  const canonicalUrl = post?.canonicalPath
    ? `${siteBase}${post.canonicalPath}`
    : `${siteBase}/blog/${slug}`;

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <SEOHead
          title="Статья не найдена | Блог Антология"
          description="Запрошенная статья блога не найдена."
          noindex
        />
        <Header />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative bg-white">
          <h1 className="text-3xl mb-4">Статья не найдена</h1>
          <Link to={NEWS_HUB_PATH} className="text-accent hover:underline text-lg">← В раздел новостей</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const renderSection = (section, index) => {
    const sectionCta = section.checklistCta ? (
      useConsultationCta ? (
        <ConsultationCtaBlock compact onOpen={() => setConsultationOpen(true)} />
      ) : (
        <ChecklistCtaBlock path={checklistPath} compact />
      )
    ) : null;

    if (section.paragraphs?.length) {
      return (
        <React.Fragment key={index}>
          <section className="mb-12 max-w-3xl">
            {section.title && <h2 className="text-2xl font-bold mb-4">{section.title}</h2>}
            {section.subtitle && (
              <div className="text-sm text-accent font-medium mb-2 uppercase tracking-wide">
                {section.subtitle}
              </div>
            )}
            {section.paragraphs.map((p, i) => (
              <p key={i} className="mb-4 text-lg text-muted-foreground leading-relaxed">
                {p}
              </p>
            ))}
            {renderDataTable(section.dataTable)}
            {section.image && (
              <BlogZoomableImage
                src={section.image}
                alt={section.imageAlt || section.title || post.title}
                className="my-6"
              />
            )}
          </section>
          {sectionCta}
        </React.Fragment>
      );
    }

    if (section.relatedLinks?.length) {
      return (
        <section key={index} className="mb-12 max-w-3xl">
          {section.title && <h2 className="text-2xl font-bold mb-4">{section.title}</h2>}
          <ul className="space-y-3">
            {section.relatedLinks.map((link, i) => (
              <li key={i}>
                <Link to={link.href} className="text-accent hover:underline text-lg">
                  {link.label} →
                </Link>
              </li>
            ))}
          </ul>
        </section>
      );
    }

    if (section.items) {
      return (
        <React.Fragment key={index}>
          <section className="mb-12 max-w-3xl">
            <h3 className="text-2xl font-bold mb-6">{section.title}</h3>
            <ul className="space-y-3">
              {section.items.map((item, i) => (
                <li key={i} className="flex gap-3 text-lg">
                  <span className="text-accent font-bold">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>
          {sectionCta}
        </React.Fragment>
      );
    }
    
    // Section with quote (blockquote)
    if (section.quote) {
      return (
        <section key={index} className="mb-12 max-w-3xl">
          <h3 className="text-2xl font-bold mb-4">{section.title}</h3>
          <div className="text-lg text-muted-foreground leading-relaxed">{formatContent(section.content)}</div>
          <blockquote className="border-l-4 border-accent pl-6 py-4 my-4 bg-accent/5 rounded-r-lg">
            <p className="text-lg italic text-foreground">"{section.quote}"</p>
            {section.source && <p className="text-sm text-muted-foreground mt-2">— {section.source}</p>}
          </blockquote>
        </section>
      );
    }
    
    // Section with table
    if (section.table) {
      return (
        <section key={index} className="mb-12 max-w-3xl">
          <h3 className="text-2xl font-bold mb-6">{section.title}</h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-semibold">Технология</th>
                  <th className="text-left py-3 px-4 font-semibold">Рост производительности</th>
                  <th className="text-left py-3 px-4 font-semibold">Период реализации</th>
                </tr>
              </thead>
              <tbody>
                {section.table.map((row, i) => (
                  <tr key={i} className="border-b border-border/50">
                    <td className="py-3 px-4">{row.tech}</td>
                    <td className="py-3 px-4 text-accent font-medium">{row.growth}</td>
                    <td className="py-3 px-4 text-muted-foreground">{row.period}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      );
    }
    
    // Section with definitions
    if (section.definitions) {
      return (
        <section key={index} className="mb-12 max-w-3xl">
          <h3 className="text-2xl font-bold mb-4">{section.title}</h3>
          <div className="text-lg text-muted-foreground leading-relaxed">{formatContent(section.content)}</div>
          <div className="space-y-4">
            {section.definitions.map((def, i) => (
              <div key={i} className="p-4 bg-card rounded-lg border border-border">
                <dt className="font-bold text-accent text-lg mb-2">{def.term}</dt>
                <dd className="text-muted-foreground">{def.description}</dd>
              </div>
            ))}
          </div>
        </section>
      );
    }
    
    // Section with stats
    if (section.stats) {
      return (
        <section key={index} className="mb-12 max-w-3xl">
          {section.subtitle && <div className="text-sm text-accent font-medium mb-2 uppercase tracking-wide">{section.subtitle}</div>}
          <h3 className="text-2xl font-bold mb-4">{section.title}</h3>
          <div className="text-lg text-muted-foreground leading-relaxed">{formatContent(section.content)}</div>
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {section.stats.map((stat, i) => (
              <div key={i} className="p-4 bg-card rounded-lg border border-border">
                <div className="text-sm text-muted-foreground">{stat.label}</div>
                <div className="text-xl font-bold text-accent">{stat.value}</div>
              </div>
            ))}
          </div>
          {section.note && <p className="text-muted-foreground italic border-l-2 border-accent/50 pl-4">{section.note}</p>}
        </section>
      );
    }
    
    return (
      <React.Fragment key={index}>
        <section className="mb-12 max-w-3xl">
          {section.subtitle && (
            <div className="text-sm text-accent font-medium mb-2 uppercase tracking-wide">
              {section.subtitle}
            </div>
          )}
          {section.title && <h3 className="text-2xl font-bold mb-4">{section.title}</h3>}
          {section.content && (
            <div className="text-lg text-muted-foreground leading-relaxed">
              {formatContent(section.content)}
            </div>
          )}
        </section>
        {sectionCta}
      </React.Fragment>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={post.seoTitle || `${post.title} | Блог Антология`}
        description={post.description}
        keywords={post.keywords || `AI для бизнеса, ${post.category}`}
        image={post.image}
        type="article"
        canonical={canonicalUrl}
        structuredData={post.structuredData}
        publishedTime={post.datePublishedISO}
        modifiedTime={post.dateModifiedISO || post.datePublishedISO}
      />
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative bg-white">
        <article className="max-w-3xl mx-auto">
          <Link to={NEWS_HUB_PATH} className="inline-flex items-center text-accent hover:underline mb-8 text-lg">
              ← В раздел новостей
            </Link>

            <header className="mb-10">
              <div className="flex items-center gap-4 mb-4">
                <Badge variant="outline" className="text-sm px-3 py-1">{post.category}</Badge>
                <span className="text-muted-foreground">{post.readTime} чтения</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">{post.title}</h1>
              {publicationLoading ? (
                <div
                  className="aspect-video mb-6 rounded-xl bg-muted animate-pulse"
                  aria-label="Загрузка изображения"
                />
              ) : post.image ? (
                <BlogZoomableImage
                  src={post.image}
                  alt={post.imageAlt || post.title}
                  className="mb-6"
                />
              ) : null}
              <p className="text-xl text-muted-foreground mb-4">{post.description}</p>
              <div className="flex items-center gap-3 text-muted-foreground flex-wrap">
                {post.author && (
                  <>
                    <Link to="/about" className="hover:text-accent transition-colors">
                      {post.author}
                    </Link>
                    <span>•</span>
                  </>
                )}
                <time dateTime={post.datePublishedISO || undefined}>{post.date}</time>
              </div>
            {post.disclaimer && (
              <p className="text-sm text-muted-foreground mt-4 italic">{post.disclaimer}</p>
            )}
            </header>

            <Separator className="mb-10" />

            {post.keyTakeaways?.length > 0 && (
              <aside
                className="mb-10 p-6 rounded-2xl border border-accent/20 bg-accent/5"
                aria-label="Кратко"
              >
                <h2 className="text-xl font-bold mb-4">Кратко</h2>
                <ul className="space-y-3 list-disc pl-5 text-muted-foreground text-lg leading-relaxed">
                  {post.keyTakeaways.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </aside>
            )}

            {(post.intro || post.intro2) && (
              <div className="mb-10">
                {post.intro && (
                  <p className="text-lg leading-relaxed mb-4 text-muted-foreground">{post.intro}</p>
                )}
                {post.intro2 && (
                  <p className="text-lg leading-relaxed mb-4 text-muted-foreground">{post.intro2}</p>
                )}
              </div>
            )}

            {post.sections.map(renderSection)}

            {post.conclusion && (
              <div className="mb-10 p-8 bg-accent/5 rounded-2xl border border-accent/20">
                <h2 className="text-2xl font-bold mb-4">Заключение</h2>
                <p className="text-lg leading-relaxed text-muted-foreground">{post.conclusion}</p>
                {post.conclusionCta && (
                  <Link
                    to={post.conclusionCta.href}
                    className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-accent text-accent-foreground font-medium rounded-lg hover:bg-accent/90 transition-colors"
                  >
                    {post.conclusionCta.label}
                  </Link>
                )}
                {post.conclusionWithChecklist && (
                  <p className="text-lg leading-relaxed text-muted-foreground mt-4">
                    Перед пилотом пройдите{' '}
                    <Link to={checklistPath} className="text-accent hover:underline font-medium">
                      чек-лист готовности к AI-внедрению
                    </Link>
                    : оцените данные, процессы и интеграции за 15–20 минут.
                  </p>
                )}
              </div>
            )}

            {post.showSources && (
              <div className="mb-10">
                <h2 className="text-2xl font-bold mb-6">Источники</h2>
                <div className="border border-border rounded-xl p-5">
                  <h3 className="text-lg font-semibold mb-2">Материалы</h3>
                  {post.sources?.length ? (
                    <ul className="space-y-2">
                      {post.sources.map((source, i) => (
                        <li key={i} className="text-muted-foreground text-base">
                          <a
                            href={source.href}
                            className="text-accent hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {source.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <>
                      <p className="text-muted-foreground text-base mb-2">
                        Токовинин: Подкаст «ИИ уже меняет мир. Как не вылететь и остаться нужным» (23 марта 2026).{' '}
                        <a
                          href="https://www.youtube.com/watch?v=xSZwNE2BtwQ"
                          className="text-accent hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          YouTube
                        </a>
                      </p>
                      <p className="text-muted-foreground text-base">
                        Нигматулин: Интервью «Академик честно: почему нас ждёт 35 лет бедности?» на канале Копанцев (1 мая 2026).{' '}
                        <a
                          href="https://rutube.ru/video/f54bafdf73dbe154f933ddb1216eaed0/"
                          className="text-accent hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Rutube
                        </a>
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}

            {post.faq?.length > 0 && (
              <div className="mb-10">
                <h2 className="text-2xl font-bold mb-6">Часто задаваемые вопросы</h2>
                <div className="space-y-4">
                  {post.faq.map((item, index) => (
                    <div key={index} className="border border-border rounded-xl p-5">
                      <h3 className="text-lg font-semibold mb-2">{item.question}</h3>
                      <p className="text-muted-foreground text-base">{item.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {useDiscussAiCta ? (
              <DiscussAiCtaBlock href={discussAiHref} />
            ) : useConsultationCta ? (
              <ConsultationCtaBlock onOpen={() => setConsultationOpen(true)} />
            ) : (
              <ChecklistCtaBlock path={checklistPath} />
            )}

            <Separator className="mb-8" />

            <Link to={NEWS_HUB_PATH} className="text-accent hover:underline text-base">← Все материалы раздела</Link>
            
            {nextPost && (
              <div className="mt-8 pt-8 border-t border-border">
                <p className="text-sm text-muted-foreground mb-2">Следующая статья</p>
                <Link to={`/blog/${nextPost.slug}`} className="text-accent hover:underline text-lg font-medium">
                  {nextPost.title} →
                </Link>
              </div>
            )}
          </article>
      </main>
      <Footer />
      <CallbackForm
        open={consultationOpen}
        onOpenChange={setConsultationOpen}
        title="Получить консультацию эксперта"
        description="Оставьте контакты, и эксперт свяжется с вами для консультации по AI-внедрению."
        submitLabel="Получить консультацию"
      />
    </div>
  );
};

export default BlogPostPage;